/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState, } from "react";
import { ChartCanvasContext } from "./ChartCanvas";
import { useEvent } from "./useEvent";
import { ChartContext } from "./Chart";
const aliases = {
    mouseleave: "mousemove",
    panend: "pan",
    pinchzoom: "pan",
    mousedown: "mousemove",
    click: "mousemove",
    contextmenu: "mousemove",
    dblclick: "mousemove",
    dragstart: "drag",
    dragend: "drag",
    dragcancel: "drag",
    zoom: "zoom",
};
export const GenericComponent = React.memo(React.forwardRef((props, ref) => {
    const context = useContext(ChartCanvasContext);
    const { chartId } = useContext(ChartContext);
    const subscriberId = useMemo(() => { var _a; return ((_a = context.generateSubscriptionId) === null || _a === void 0 ? void 0 : _a.call(context)) || 0; }, []);
    const [, setUpdateCount] = useState(0);
    const { subscribe, unsubscribe } = context;
    const { clip = true, edgeClip = false, canvasToDraw = (contexts) => contexts.mouseCoord, } = props;
    const moreProps = React.useRef({
        chartId: context.chartId,
        hovering: false,
        currentCharts: [],
        chartConfigs: context.chartConfigs,
        fullData: context.fullData,
        plotData: context.plotData,
        xScale: context.xScale,
        xAccessor: context.xAccessor,
    });
    const dragInProgressRef = useRef(false);
    const evaluationInProgressRef = useRef(false);
    const iSetTheCursorClassRef = useRef(false);
    const updateMoreProps = useCallback((newMoreProps, moreProps) => {
        var _a;
        Object.assign(moreProps, newMoreProps || {});
        (_a = props.updateMoreProps) === null || _a === void 0 ? void 0 : _a.call(props, newMoreProps, moreProps);
    }, [props.updateMoreProps]);
    const getMoreProps = useCallback(() => {
        var _a;
        const { chartConfigs, xAccessor, displayXAccessor, width, height, fullData } = context;
        const otherMoreProps = (_a = props.getMoreProps) === null || _a === void 0 ? void 0 : _a.call(props, moreProps.current);
        return Object.assign(Object.assign(Object.assign({ displayXAccessor,
            width,
            height }, moreProps.current), { fullData,
            chartConfigs,
            xAccessor }), otherMoreProps);
    }, [context, props.getMoreProps]);
    useImperativeHandle(ref, () => ({
        getMoreProps,
    }), [getMoreProps]);
    const isHover = useCallback((e) => {
        if (props.isHover === undefined) {
            return false;
        }
        return props.isHover(getMoreProps(), e);
    }, [props.isHover, getMoreProps]);
    const preCanvasDraw = useCallback((ctx, moreProps) => {
        var _a;
        (_a = props.preCanvasDraw) === null || _a === void 0 ? void 0 : _a.call(props, ctx, moreProps);
    }, [props.preCanvasDraw]);
    const postCanvasDraw = useCallback((ctx, moreProps) => {
        var _a;
        (_a = props.postCanvasDraw) === null || _a === void 0 ? void 0 : _a.call(props, ctx, moreProps);
    }, [props.postCanvasDraw]);
    const evaluateType = useEvent((type, e) => {
        const newType = aliases[type] || type;
        const proceed = props.drawOn.includes(newType);
        if (!proceed) {
            return;
        }
        if (props.shouldTypeProceed && !props.shouldTypeProceed(type, moreProps.current)) {
            return;
        }
        switch (type) {
            case "zoom":
            case "mouseenter":
                // DO NOT DRAW FOR THESE EVENTS
                break;
            case "mouseleave": {
                moreProps.current.hovering = false;
                if (props.onUnHover) {
                    props.onUnHover(e, getMoreProps());
                }
                break;
            }
            case "contextmenu": {
                if (props.onContextMenu) {
                    props.onContextMenu(e, getMoreProps());
                }
                if (moreProps.current.hovering && props.onContextMenuWhenHover) {
                    props.onContextMenuWhenHover(e, getMoreProps());
                }
                break;
            }
            case "mousedown": {
                if (props.onMouseDown) {
                    props.onMouseDown(e, getMoreProps());
                }
                break;
            }
            case "click": {
                const { onClick, onClickOutside, onClickWhenHover } = props;
                const moreProps = getMoreProps();
                if (moreProps.hovering && onClickWhenHover !== undefined) {
                    onClickWhenHover(e, moreProps);
                }
                else if (onClickOutside !== undefined) {
                    onClickOutside(e, moreProps);
                }
                if (onClick !== undefined) {
                    onClick(e, moreProps);
                }
                break;
            }
            case "mousemove": {
                const prevHover = moreProps.current.hovering;
                moreProps.current.hovering = isHover(e);
                const { amIOnTop, setCursorClass } = context;
                if (moreProps.current.hovering &&
                    !props.selected &&
                    /* && !prevHover */
                    amIOnTop(subscriberId) &&
                    props.onHover !== undefined) {
                    setCursorClass("react-financial-charts-pointer-cursor");
                    iSetTheCursorClassRef.current = true;
                }
                else if (moreProps.current.hovering && props.selected && amIOnTop(subscriberId)) {
                    setCursorClass(props.interactiveCursorClass);
                    iSetTheCursorClassRef.current = true;
                }
                else if (prevHover && !moreProps.current.hovering && iSetTheCursorClassRef.current) {
                    iSetTheCursorClassRef.current = false;
                    setCursorClass(null);
                }
                const morePropsSub = getMoreProps();
                if (moreProps.current.hovering && !prevHover) {
                    if (props.onHover) {
                        props.onHover(e, morePropsSub);
                    }
                }
                if (prevHover && !moreProps.current.hovering) {
                    if (props.onUnHover) {
                        props.onUnHover(e, morePropsSub);
                    }
                }
                if (props.onMouseMove) {
                    props.onMouseMove(e, morePropsSub);
                }
                break;
            }
            case "dblclick": {
                const morePropsSub = getMoreProps();
                if (props.onDoubleClick) {
                    props.onDoubleClick(e, morePropsSub);
                }
                if (moreProps.current.hovering && props.onDoubleClickWhenHover) {
                    props.onDoubleClickWhenHover(e, morePropsSub);
                }
                break;
            }
            case "pan": {
                moreProps.current.hovering = false;
                if (props.onPan) {
                    props.onPan(e, getMoreProps());
                }
                break;
            }
            case "panend": {
                if (props.onPanEnd) {
                    props.onPanEnd(e, getMoreProps());
                }
                break;
            }
            case "dragstart": {
                if (getPanConditions().draggable) {
                    const { amIOnTop } = context;
                    if (amIOnTop(subscriberId)) {
                        dragInProgressRef.current = true;
                        if (props.onDragStart !== undefined) {
                            props.onDragStart(e, getMoreProps());
                        }
                    }
                }
                break;
            }
            case "drag": {
                if (dragInProgressRef.current && props.onDrag) {
                    props.onDrag(e, getMoreProps());
                }
                break;
            }
            case "dragend": {
                if (dragInProgressRef.current && props.onDragComplete) {
                    props.onDragComplete(e, getMoreProps());
                }
                dragInProgressRef.current = false;
                break;
            }
            case "dragcancel": {
                if (dragInProgressRef.current || iSetTheCursorClassRef.current) {
                    const { setCursorClass } = context;
                    setCursorClass(null);
                }
                break;
            }
        }
    });
    const listener = useEvent((type, newMoreProps, state, e) => {
        if (newMoreProps) {
            updateMoreProps(newMoreProps, moreProps.current);
        }
        evaluationInProgressRef.current = true;
        evaluateType(type, e);
        evaluationInProgressRef.current = false;
    });
    const drawOnCanvas = useCallback(() => {
        var _a;
        const { canvasDraw } = props;
        if (canvasDraw === undefined || canvasToDraw === undefined) {
            return;
        }
        const moreProps = getMoreProps();
        const contexts = (_a = context.getCanvasContexts) === null || _a === void 0 ? void 0 : _a.call(context);
        if (contexts === undefined) {
            return;
        }
        const ctx = canvasToDraw(contexts);
        if (ctx !== undefined) {
            preCanvasDraw(ctx, moreProps);
            canvasDraw(ctx, moreProps);
            postCanvasDraw(ctx, moreProps);
        }
    }, [canvasToDraw, props.canvasDraw, context.getCanvasContexts, preCanvasDraw, postCanvasDraw, getMoreProps]);
    const draw = useEvent(({ trigger, force = false }) => {
        const type = aliases[trigger] || trigger;
        const proceed = props.drawOn.indexOf(type) > -1;
        if (proceed || props.selected /* this is to draw as soon as you select */ || force) {
            const { canvasDraw } = props;
            if (canvasDraw === undefined) {
                setUpdateCount((u) => u + 1);
            }
            else {
                drawOnCanvas();
            }
        }
    });
    const getPanConditions = useEvent(() => {
        const draggable = (props.selected && moreProps.current.hovering) ||
            (props.enableDragOnHover && moreProps.current.hovering);
        return {
            draggable: !!draggable,
            panEnabled: !props.disablePan,
        };
    });
    useEffect(() => {
        const { setCursorClass } = context;
        if (props.selected && moreProps.current.hovering) {
            iSetTheCursorClassRef.current = true;
            setCursorClass(props.interactiveCursorClass);
        }
        else {
            iSetTheCursorClassRef.current = false;
            setCursorClass(null);
        }
    }, [props.selected]);
    useEffect(() => {
        if (props.canvasDraw !== undefined && !evaluationInProgressRef.current) {
            updateMoreProps(undefined, moreProps.current);
            drawOnCanvas();
        }
    });
    useEffect(() => {
        subscribe(subscriberId, {
            chartId,
            clip,
            edgeClip,
            listener,
            draw,
            getPanConditions,
        });
        return () => {
            unsubscribe(subscriberId);
            if (iSetTheCursorClassRef.current) {
                context.setCursorClass(null);
            }
        };
    }, [chartId, subscriberId, edgeClip, clip]);
    const { canvasDraw, svgDraw } = props;
    if (canvasDraw !== undefined || svgDraw === undefined) {
        return null;
    }
    const suffix = chartId !== undefined ? "-" + chartId : "";
    const style = clip ? { clipPath: `url(#chart-area-clip${suffix})` } : undefined;
    return React.createElement("g", { style: style }, svgDraw(getMoreProps()));
}));
export const getAxisCanvas = (contexts) => {
    return contexts.axes;
};
export const getMouseCanvas = (contexts) => {
    return contexts.mouseCoord;
};
//# sourceMappingURL=GenericComponent.js.map